import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { portalApi, portalError } from "./portalApi";
import { Btn, Input, Label, Modal, Badge, Empty } from "../components/UI";
import { Pill, RefreshCw, Store } from "lucide-react";
import { toast } from "sonner";

export default function PortalMedications() {
  const [meds, setMeds] = useState([]);
  const [refills, setRefills] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [refillFor, setRefillFor] = useState(null);
  const [form, setForm] = useState({ pharmacy: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    portalApi.get("/portal/medications").then((r) => setMeds(r.data));
    portalApi.get("/portal/refill-requests").then((r) => setRefills(r.data));
    portalApi.get("/portal/pharmacies").then((r) => setPharmacies(r.data)).catch(() => setPharmacies([]));
  };
  useEffect(() => { load(); }, []);

  const preferredPharmacyName = () => {
    const preferred = pharmacies.find((p) => p.preferred);
    if (preferred) return preferred.name;
    if (pharmacies[0]) return pharmacies[0].name;
    return "";
  };

  const openRefill = (m) => {
    // Prefill preferred pharmacy so the client does not have to retype it.
    setForm({ pharmacy: preferredPharmacyName(), notes: "" });
    setRefillFor(m);
  };

  const requestRefill = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/refill-requests", { medication_id: refillFor.id, ...form });
      toast.success("Refill request sent to your care team");
      setRefillFor(null); load();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  const pendingIds = new Set(refills.filter((r) => r.status === "pending").map((r) => r.medication_id));

  return (
    <div className="space-y-6" data-testid="portal-medications-page">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#3E2723]">My Medications</h1>
          <p className="text-sm text-[#7B6157]">Current prescriptions & refill requests. Always follow your prescriber&apos;s directions.</p>
        </div>
        {pharmacies.length === 0 && (
          <a href="/portal/pharmacies" data-testid="portal-med-set-pharmacy"
            className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] px-3 py-1.5 text-xs font-bold text-[#8D5A00] hover:bg-[#FFE0B2]">
            <Store size={12} /> Set a preferred pharmacy
          </a>
        )}
      </div>

      {meds.length === 0 ? <Empty text="No medications on file" /> : (
        <div className="grid gap-3 sm:grid-cols-2">
          {meds.map((m) => (
            <div key={m.id} className="rounded-2xl bg-white border border-[#EFEBE0] p-4" data-testid={`portal-med-${m.id}`}>
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E0F2F1] text-[#00695C]"><Pill size={16} /></span>
                <div className="min-w-0">
                  <div className="font-bold text-[#3E2723]">{m.medication}</div>
                  <div className="text-xs text-[#7B6157]">{m.dosage} · {m.frequency} · {m.route}</div>
                  <div className="text-[11px] text-[#84675B] mt-0.5">Prescribed by {m.prescriber || "-"}{m.start_date ? ` · since ${dayjs(m.start_date).format("MMM YYYY")}` : ""}</div>
                </div>
                <span className="ml-auto"><Badge value={m.status || ""} /></span>
              </div>
              {(m.status || "").toLowerCase() === "active" && (
                pendingIds.has(m.id) ? (
                  <div className="mt-3 rounded-lg bg-[#FFF8E1] px-3 py-1.5 text-[11px] font-bold text-[#8D6E00]">Refill request pending</div>
                ) : (
                  <button data-testid={`portal-refill-btn-${m.id}`} onClick={() => openRefill(m)}
                    className="mt-3 flex items-center gap-1.5 rounded-full bg-[#4E342E] px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#5D4037] transition-colors">
                    <RefreshCw size={12} /> Request Refill
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {refills.length > 0 && (
        <div>
          <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">Refill History</h3>
          <div className="space-y-2">
            {refills.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-white border border-[#EFEBE0] px-4 py-2.5 text-sm" data-testid={`portal-refill-${r.id}`}>
                <span className="font-semibold text-[#3E2723]">{r.medication}</span>
                <span className="text-xs text-[#7B6157]">{r.pharmacy}</span>
                <span className="text-[11px] text-[#84675B]">{dayjs(r.created_at).format("MMM D")}</span>
                <span className="ml-auto"><Badge value={r.status} /></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal open={!!refillFor} onClose={() => setRefillFor(null)} title={refillFor ? `Refill: ${refillFor.medication}` : ""}>
        {refillFor && (
          <form onSubmit={requestRefill} className="space-y-4">
            <div className="rounded-xl bg-[#F5F2EB] p-3 text-sm text-[#5D4037]">{refillFor.medication} {refillFor.dosage} - {refillFor.frequency}</div>
            <div>
              <Label>Pharmacy</Label>
              {pharmacies.length > 0 ? (
                <>
                  <div className="mt-1 flex flex-wrap gap-1.5" data-testid="portal-refill-pharmacy-chips">
                    {pharmacies.map((p) => {
                      const active = form.pharmacy === p.name;
                      return (
                        <button key={p.id} type="button" data-testid={`portal-refill-pharm-chip-${p.id}`}
                          onClick={() => setForm({ ...form, pharmacy: p.name })}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${active ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#5D4037] hover:bg-[#EDE7DC]"}`}>
                          {p.preferred && <span className="mr-1">★</span>}{p.name}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1 text-[11px] text-[#7B6157]">Preferred pharmacy is prefilled. Tap another to switch.</p>
                </>
              ) : (
                <Input data-testid="portal-refill-pharmacy" value={form.pharmacy} onChange={(e) => setForm({ ...form, pharmacy: e.target.value })} placeholder="Enter your pharmacy" required />
              )}
            </div>
            <div><Label>Notes (optional)</Label><Input data-testid="portal-refill-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g., running low, travelling next week" /></div>
            <div className="flex justify-end gap-2">
              <Btn type="button" variant="ghost" onClick={() => setRefillFor(null)}>Cancel</Btn>
              <Btn data-testid="portal-refill-submit" type="submit" disabled={busy || !form.pharmacy.trim()}>{busy ? "Sending…" : "Send Request"}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
